import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import shp from 'shpjs';
import JSZip from 'jszip';
import { gpx, kml } from '@tmcw/togeojson';
import 'leaflet/dist/leaflet.css';

const SUPPORTED_EXTENSIONS = new Set(['kml', 'kmz', 'zip', 'shp', 'geojson', 'gpx']);

const getExtensao = (arquivo) => String(arquivo || '').split('.').pop().toLowerCase();

function parseXml(texto) {
  const xml = new DOMParser().parseFromString(texto, 'text/xml');
  if (xml.querySelector('parsererror')) throw new Error('O arquivo XML geográfico não é válido.');
  return xml;
}

function juntarColecoes(resultado) {
  if (!Array.isArray(resultado)) return resultado;
  return {
    type: 'FeatureCollection',
    features: resultado.flatMap((colecao) => colecao?.features || [])
  };
}

async function carregarGeoJson(url, extensao) {
  const resposta = await fetch(url);
  if (!resposta.ok) throw new Error('Não foi possível carregar o arquivo geográfico.');

  if (extensao === 'geojson') return resposta.json();
  if (extensao === 'kml') return kml(parseXml(await resposta.text()));
  if (extensao === 'gpx') return gpx(parseXml(await resposta.text()));

  const buffer = await resposta.arrayBuffer();
  if (extensao === 'zip') return juntarColecoes(await shp(buffer));
  if (extensao === 'shp') return juntarColecoes(await shp({ shp: buffer }));

  const zip = await JSZip.loadAsync(buffer);
  const arquivoKml = Object.values(zip.files).find((arquivo) => !arquivo.dir && arquivo.name.toLowerCase().endsWith('.kml'));
  if (!arquivoKml) throw new Error('O pacote KMZ não contém um arquivo KML.');
  return kml(parseXml(await arquivoKml.async('text')));
}

const DocumentoGisPreview = ({ arquivo, titulo, url }) => {
  const mapContainerRef = useRef(null);
  const [geoJson, setGeoJson] = useState(null);
  const [erro, setErro] = useState('');
  const [linksExternos, setLinksExternos] = useState(null);
  const extensao = getExtensao(arquivo);

  useEffect(() => {
    let ativo = true;
    setGeoJson(null);
    setErro('');
    setLinksExternos(null);

    if (!SUPPORTED_EXTENSIONS.has(extensao)) {
      setErro('Este componente do shapefile não pode ser exibido isoladamente. Envie um pacote ZIP com os arquivos geográficos relacionados.');
      return () => { ativo = false; };
    }

    carregarGeoJson(url, extensao)
      .then((resultado) => {
        if (!ativo) return;
        if (!resultado?.features?.length) throw new Error('O arquivo geográfico não possui feições para exibir.');
        setGeoJson(resultado);
      })
      .catch((err) => {
        if (ativo) setErro(err?.message || 'Não foi possível interpretar o arquivo geográfico.');
      });

    return () => { ativo = false; };
  }, [extensao, url]);

  useEffect(() => {
    if (!geoJson || !mapContainerRef.current) return undefined;

    const map = L.map(mapContainerRef.current, { scrollWheelZoom: false });
    const mapaPadrao = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: 19
    }).addTo(map);
    const mapaSatelite = L.tileLayer('https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
      attribution: 'Tiles &copy; Esri',
      maxZoom: 19
    });

    L.control.layers({
      Mapa: mapaPadrao,
      Satélite: mapaSatelite
    }, null, { collapsed: false }).addTo(map);

    const layer = L.geoJSON(geoJson, {
      pointToLayer: (_feature, latlng) => L.circleMarker(latlng, {
        radius: 6,
        color: '#166534',
        fillColor: '#22c55e',
        fillOpacity: 0.85
      }),
      style: {
        color: '#166534',
        fillColor: '#22c55e',
        fillOpacity: 0.25,
        weight: 3
      }
    }).addTo(map);

    const bounds = layer.getBounds();
    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [20, 20] });
      const centro = bounds.getCenter();
      const coordenadas = `${centro.lat.toFixed(7)},${centro.lng.toFixed(7)}`;
      setLinksExternos({
        googleMaps: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(coordenadas)}`,
        googleEarth: `https://earth.google.com/web/search/${encodeURIComponent(coordenadas)}`
      });
    } else {
      map.setView([-15.601, -56.097], 5);
    }

    return () => map.remove();
  }, [geoJson]);

  if (erro) return <p className="modal-documento-preview-empty">{erro}</p>;
  if (!geoJson) return <p className="modal-documento-preview-loading">Carregando visualização GIS...</p>;

  return (
    <div>
      <div ref={mapContainerRef} className="modal-documento-preview-map" aria-label={`Mapa do arquivo ${titulo || arquivo}`} />
      <p className="modal-documento-preview-attribution">Visualização GIS com Leaflet. Use o seletor no mapa para alternar entre mapa e satélite.</p>
      {linksExternos && (
        <div className="modal-documento-preview-external-actions">
          <a href={linksExternos.googleMaps} target="_blank" rel="noopener noreferrer" className="btn btn-secondary">
            Abrir no Google Maps
          </a>
          <a href={linksExternos.googleEarth} target="_blank" rel="noopener noreferrer" className="btn btn-secondary">
            Abrir no Google Earth
          </a>
        </div>
      )}
    </div>
  );
};

export default DocumentoGisPreview;
